import { Link } from "@inertiajs/react"
import { Badge } from "@/components/ui/Badge"
import { Card } from "@/components/ui/Card"
import { flagForCurrency } from "@/components/ui/Flag"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/Table"
import { formatMoney } from "@/lib/money"
import type { MarketRow, PlanState } from "../data"
import Delta from "@/components/home/Delta"
import TargetBar from "@/components/home/TargetBar"

const PLAN_BADGE: Record<PlanState, { label: string; variant: "success" | "warning" | "outline" }> = {
  published: { label: "Published", variant: "success" },
  in_review: { label: "Revision in review", variant: "warning" },
  none: { label: "No plan yet", variant: "outline" },
}

export default function MarketsTable({ rows }: { rows: MarketRow[] }) {
  return (
    <Card className="px-5 pb-2 pt-4">
      <div className="text-sm font-semibold">Markets</div>
      <div className="mb-2 text-xs text-muted-foreground">Each market in its own currency. Click a market to open it.</div>
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="h-9 px-2 text-foreground">Market</TableHead>
            <TableHead className="h-9 px-2 text-right text-foreground">Revenue this month</TableHead>
            <TableHead className="h-9 px-2 text-foreground">vs last month</TableHead>
            <TableHead className="h-9 w-48 px-2 text-foreground">This year vs target</TableHead>
            <TableHead className="h-9 px-2 text-right text-foreground">Need buying</TableHead>
            <TableHead className="h-9 px-2 text-right text-foreground">Excess stock</TableHead>
            <TableHead className="h-9 px-2 text-foreground">Plan</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((m) => {
            const Flag = flagForCurrency(m.currency)
            const plan = PLAN_BADGE[m.plan]
            return (
              <TableRow key={m.slug}>
                <TableCell className="px-2 py-3">
                  <Link href="/wireframes/home/market" className="inline-flex items-center gap-2 font-medium text-link underline-offset-4 hover:underline">
                    {Flag && <Flag size={16} />}
                    {m.name}
                  </Link>
                </TableCell>
                <TableCell className="px-2 py-3 text-right font-medium tabular-nums">{formatMoney(m.revenueThisMonth, m.currency)}</TableCell>
                <TableCell className="px-2 py-3"><Delta pct={m.changePct} /></TableCell>
                <TableCell className="px-2 py-3"><TargetBar actual={m.revenueThisYear} target={m.yearTarget} currency={m.currency} compact /></TableCell>
                <TableCell className="px-2 py-3 text-right font-medium tabular-nums text-warning">{m.needBuying}</TableCell>
                <TableCell className="px-2 py-3 text-right tabular-nums">{m.excessStock}</TableCell>
                <TableCell className="px-2 py-3"><Badge variant={plan.variant}>{plan.label}</Badge></TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Card>
  )
}
